import React, { useState } from 'react';
import TimeRange from './TimeRange';
import useFrenchField from './frenchField';
import { saveSubtitleEdit } from './subtitleStore';
import { useTimeline } from './timelineContext';
import { nextStartAfter, followingStart } from '../../utils/timeline';
import { formatTime } from './momentTime';
import './SubtitlePanel.css';

/**
 * SubtitleEditForm — edit a subtitle's window and text. Changing its times
 * moves the annotations in its old window with it (subtitleStore); changing
 * its end moves the next subtitle's start with it when they touched or would
 * overlap, as the form says before saving (Requirement 59).
 *
 * Props:
 *   subtitle, currentTime
 *   onSaved(updated, movedAnnotations, neighbours), onCancel()
 */
function SubtitleEditForm({ subtitle, currentTime, onSaved, onCancel }) {
  const [start, setStart] = useState(subtitle.startTime);
  const { subtitles } = useTimeline();
  const [end, setEnd] = useState(subtitle.endTime ?? null);
  const [originalText, setOriginalText] = useState(subtitle.originalText || '');
  const [englishText, setEnglishText] = useState(subtitle.englishText || '');
  const [showFrench, setShowFrench] = useFrenchField();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The next subtitle's start, if it follows this end.
  const follow = end !== subtitle.endTime ? followingStart(subtitle, { startTime: start, endTime: end }, subtitles) : null;

  // French is required only while its field is shown; English always.
  const canSave = (!showFrench || originalText.trim()) && englishText.trim() && (end === null || end >= start)
    && !(follow && follow.blocked) && !saving;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError('');
    const changes = {
      startTime: start,
      endTime: end,
      originalText: originalText.trim(),
      englishText: englishText.trim(),
    };
    try {
      const { moved, neighbours } = await saveSubtitleEdit(subtitle, changes, subtitles);
      onSaved({ ...subtitle, ...changes }, moved, neighbours);
    } catch (err) {
      console.error('Error saving subtitle:', err);
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <form className="subtitle-form" onSubmit={handleSave} aria-label="Edit subtitle">
      <TimeRange
        start={start}
        end={end}
        onChange={(w) => { setStart(w.start); setEnd(w.end); }}
        currentTime={currentTime}
        optionalEnd
        nextStart={nextStartAfter(start, subtitles.filter((s) => s.id !== subtitle.id))}
        testIdPrefix="subtitle"
      />

      {follow && (
        <p className={`subtitle-form-follow${follow.blocked ? ' subtitle-form-error' : ''}`} role="status">
          {follow.blocked
            ? `The next subtitle ends at ${formatTime(follow.next.endTime)}, so it can't start at ${formatTime(follow.startTime)}. Change its end first, or end this one earlier.`
            : `The next subtitle (${formatTime(follow.next.startTime)}) will start at ${formatTime(follow.startTime)}, with its words.`}
        </p>
      )}

      <label className="subtitle-form-toggle" title="Untick when the video already has French captions on YouTube">
        <input type="checkbox" checked={showFrench} onChange={(e) => setShowFrench(e.target.checked)} />
        Include French text
      </label>

      {showFrench && (
        <div className="subtitle-form-field">
          <label className="subtitle-form-label">French text *</label>
          <input
            className="subtitle-form-input"
            type="text"
            placeholder="Subtitle in French"
            value={originalText}
            onChange={(e) => setOriginalText(e.target.value)}
            aria-label="French subtitle text"
          />
        </div>
      )}

      <div className="subtitle-form-field">
        <label className="subtitle-form-label">English text *</label>
        <input
          className="subtitle-form-input"
          type="text"
          placeholder="English translation"
          value={englishText}
          onChange={(e) => setEnglishText(e.target.value)}
          aria-label="English translation"
        />
      </div>

      {error && <p className="subtitle-form-error">{error}</p>}

      <div className="subtitle-form-actions">
        <button type="submit" className="subtitle-form-save-btn" disabled={!canSave}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="subtitle-form-cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default SubtitleEditForm;
